import { defineStore } from 'pinia';

type IConsentStorage = 'granted' | 'denied';

export interface IConsentSettings {
  functionality_storage: IConsentStorage;
  analytics_storage: IConsentStorage;
  ad_storage: IConsentStorage;
  ad_user_data: IConsentStorage;
  ad_personalization: IConsentStorage;
  personalization_storage: IConsentStorage;
  security_storage: IConsentStorage;
}

interface IEcommerceItem {
  item_name: string;
  item_id: string;
  unit_type: string;
  move_in_date: string;
  billing_period: string;
  insurance: string;
  products: string[];
}

export interface IEcommerceData {
  currency: string;
  value: number;
  quantity: number;
  items: IEcommerceItem[];
  payment_type?: string;
  transaction_id?: string;
  tax?: number;
  coupon?: string;
}

interface IEnhancedConversionData {
  email?: string;
  phone?: string;
  name?: string;
}

interface IDataLayerConsent {
  functionality_storage: IConsentStorage;
  analytics_storage: IConsentStorage;
  ad_storage: IConsentStorage;
  personalization_storage: IConsentStorage;
  security_storage: IConsentStorage;
}

interface IDataLayerEvent {
  event?: string;
  defaultConsent?: IDataLayerConsent;
  updateConsent?: IDataLayerConsent;
  pageUrl?: string;
  pageTitle?: string;
  ecommerce?: IEcommerceData | null;
  enhanced_conversion_data?: IEnhancedConversionData;
}

export const useTrackingStore = defineStore('tracking', () => {
  // Ensure dataLayer is initialized
  if (!window.dataLayer) {
    window.dataLayer = [];
  }

  function pushToDataLayer(event: IDataLayerEvent) {
    window.dataLayer.push(event);
  }

  function updateConsent(settings: IConsentSettings) {
    gtag('consent', 'update', settings);
  }

  function pushVirtualPageview(pageTitle: string) {
    pushToDataLayer({
      event: 'virtualPageview',
      pageUrl: window.location.href,
      pageTitle,
    });
  }

  function pushAddToCart(
    data: IEcommerceData,
    enhancedData?: IEnhancedConversionData
  ) {
    pushToDataLayer({
      ecommerce: null,
    });
    pushToDataLayer({
      event: 'add_to_cart',
      ecommerce: data,
      enhanced_conversion_data: enhancedData,
    });
  }

  function pushBeginCheckout(
    data: IEcommerceData,
    enhancedData?: IEnhancedConversionData
  ) {
    pushToDataLayer({
      event: 'begin_checkout',
      ecommerce: data,
      enhanced_conversion_data: enhancedData,
    });
  }

  function pushAddShippingInfo(
    data: IEcommerceData,
    enhancedData?: IEnhancedConversionData
  ) {
    pushToDataLayer({
      event: 'add_shipping_info',
      ecommerce: data,
      enhanced_conversion_data: enhancedData,
    });
  }

  function pushAddPaymentInfo(
    data: IEcommerceData,
    enhancedData?: IEnhancedConversionData
  ) {
    pushToDataLayer({
      event: 'add_payment_info',
      ecommerce: data,
      enhanced_conversion_data: enhancedData,
    });
  }

  function pushPurchase(
    data: IEcommerceData,
    enhancedData?: IEnhancedConversionData
  ) {
    pushToDataLayer({
      event: 'purchase',
      ecommerce: data,
      enhanced_conversion_data: enhancedData,
    });
  }

  function resetEcommerceData() {
    // First, clear any existing ecommerce and enhanced conversion data
    pushToDataLayer({
      ecommerce: null,
      enhanced_conversion_data: undefined,
    });
  }

  return {
    // Actions
    updateConsent,
    pushVirtualPageview,
    pushAddToCart,
    pushBeginCheckout,
    pushAddShippingInfo,
    pushAddPaymentInfo,
    pushPurchase,
    resetEcommerceData,
  };
});
